import React from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DRAG_HANDLE_CLASS } from './WidgetFrame';

const AutoWidthGrid = WidthProvider(GridLayout);

export const GRID_COLS = 12;
const ROW_HEIGHT = 72;
const MARGIN = [12, 12];
const MIN_W = 2;
const MIN_H = 2;

const toInt = (v, d) => (Number.isFinite(Number(v)) ? Math.max(0, Math.round(Number(v))) : d);

/** Spec widget layout → react-grid-layout item; missing layouts are packed 2-per-row. */
export const layoutFromWidgets = (widgets) =>
  (widgets || []).map((w, i) => {
    const l = w.layout || {};
    return {
      i: w.widget_id,
      x: toInt(l.x, (i % 2) * 6),
      y: toInt(l.y, Math.floor(i / 2) * 4),
      w: Math.min(GRID_COLS, Math.max(MIN_W, toInt(l.w, 6))),
      h: Math.max(MIN_H, toInt(l.h, 4)),
      minW: MIN_W,
      minH: MIN_H,
    };
  });

/**
 * 12-column drag/resize grid. It never writes the spec: on drag/resize stop it
 * hands the full layout to `onLayoutCommit`, and the parent turns the diff into
 * one debounced `set_layout` EditOp batch. Below `md` the grid becomes a single
 * read-only column (no gestures on phones).
 *
 * @param resetKey  bump to force the grid back to the spec layout (failed edit)
 */
export default function DashboardGrid({ widgets, renderWidget, canEdit, onLayoutCommit, resetKey }) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const layout = React.useMemo(() => layoutFromWidgets(widgets), [widgets]);

  if (!widgets || widgets.length === 0) return null;

  if (narrow) {
    const ordered = layout.slice().sort((a, b) => a.y - b.y || a.x - b.x);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {ordered.map((item) => {
          const w = widgets.find((x) => x.widget_id === item.i);
          return (
            <Box key={item.i} sx={{ height: Math.max(280, item.h * ROW_HEIGHT) }}>
              {renderWidget(w, false)}
            </Box>
          );
        })}
      </Box>
    );
  }

  const commit = (next) => onLayoutCommit && onLayoutCommit(next);

  return (
    <Box sx={{ mx: -1.5, '& .react-grid-item.react-grid-placeholder': { bgcolor: 'primary.light', borderRadius: 2 } }}>
      <AutoWidthGrid
        key={resetKey}
        className="dashboard-grid"
        layout={layout}
        cols={GRID_COLS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        isDraggable={!!canEdit}
        isResizable={!!canEdit}
        draggableHandle={`.${DRAG_HANDLE_CLASS}`}
        compactType="vertical"
        onDragStop={commit}
        onResizeStop={commit}
      >
        {widgets.map((w) => (
          <div key={w.widget_id}>{renderWidget(w, canEdit)}</div>
        ))}
      </AutoWidthGrid>
    </Box>
  );
}
